import { useCallback, useState, useEffect } from 'react';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { InputGroup } from 'foundations-components/transitional/components/InputGroup';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Select } from 'foundations-components/transitional/components/Select';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { VisualTypes } from 'reporting-snowflake/relational/schema/visual-records';
import { getReportVisualType } from 'reporting-snowflake/relational/utils/report-utils';
import { getCurrentBreakdownColumn } from '../../breakdown-editor-dropdown/utils';
import { useRelationalReportData, useRelationalReportInfrastructure, useRelationalReportProperties, useRelationalReportAdvancedProperties } from '../../../contexts/RelationalReportContext';
import useSRVSnowflakeUpdater from '../../../hooks/relationalReports/useSRVSnowflakeUpdater';
import { getPropertyValue } from '../../../lib/relational-report-utils/property-utils';
import { buildAddColumnParams, buildRemoveColumnParams, buildUpdateColumnParams } from '../../../lib/relational-report-utils/snowflake-updater-utils';
import getMeasureSizeRanges from '../../../lib/report-utils/getMeasureSizeRanges';
import { shouldHideNoneOption } from '../../../lib/shared-utils';
import DataColumnsHintFooter from '../../shared-components/dropdown/DataColumnsHintFooter';
import ErrorAlert from '../../shared-components/ErrorAlert';
import { jsx as _jsx } from "react/jsx-runtime";
const RelationalReportDimensionEditorDropdown = props => {
  const {
    report,
    dropdownFooter = /*#__PURE__*/_jsx(DataColumnsHintFooter, {}),
    hideClearButton = false,
    buttonUse,
    onChange
  } = props;
  const [propertyValue, setPropertyValue] = useState('');
  const [error, setError] = useState(null);
  const {
    relationalReport,
    reportColumns: {
      currentDimensionColumns,
      allAvailableColumns,
      allStagedColumns
    },
    isLoading
  } = useRelationalReportData();
  const {
    fullTableAndPropertyMeta: {
      data: tableAndPropertyMeta,
      error: fullTableAndPropertyMetaError
    }
  } = useRelationalReportInfrastructure();
  const {
    availablePropertiesOptionsWithoutDefaultMeasures
  } = useRelationalReportProperties();
  const {
    allDatasetFields
  } = useRelationalReportAdvancedProperties();
  const {
    addColumnToChannel,
    moveColumnToStagedColumns,
    removeColumnFromReportColumns,
    updateEncodedColumnWithExpressionAddStaged,
    updateEncodedColumnWithExpression
  } = useSRVSnowflakeUpdater(report);
  const chartType = getReportVisualType(relationalReport);
  const {
    minDimensions
  } = getMeasureSizeRanges(relationalReport);
  const hideNoneOptionInUISelect = shouldHideNoneOption({
    report,
    minDimensions,
    hideClearButton
  });
  const currentBreakdownColumn = getCurrentBreakdownColumn(relationalReport);
  const availableDimensionColumns = currentBreakdownColumn ? currentDimensionColumns.filter(column => column.get('alias') !== currentBreakdownColumn.get('alias')) : currentDimensionColumns;
  const currentDimensionColumn = availableDimensionColumns.first();
  useEffect(() => {
    if (currentDimensionColumn) {
      setPropertyValue(getPropertyValue(currentDimensionColumn));
    } else {
      setPropertyValue('');
    }
  }, [currentDimensionColumn]);
  const onPropertyChange = useCallback(({
    target: {
      value
    }
  }) => {
    if (!tableAndPropertyMeta) {
      return;
    }
    let result;
    if (!currentDimensionColumn) {
      const {
        column,
        channel
      } = buildAddColumnParams(value, allAvailableColumns, relationalReport, tableAndPropertyMeta, allDatasetFields);
      result = addColumnToChannel(column, channel);
    } else if (!value) {
      const {
        action,
        params
      } = buildRemoveColumnParams(currentDimensionColumn, relationalReport, allStagedColumns, tableAndPropertyMeta, allDatasetFields);
      if (action === 'moveToStaged') {
        result = moveColumnToStagedColumns(params.originalColumn, params.newColumnForStagedColumns);
      } else {
        result = removeColumnFromReportColumns(params.column);
      }
    } else {
      const {
        action,
        params
      } = buildUpdateColumnParams(value, currentDimensionColumn, relationalReport, allAvailableColumns, allStagedColumns, tableAndPropertyMeta, allDatasetFields);
      if (action === 'updateWithStaged') {
        result = updateEncodedColumnWithExpressionAddStaged(params.alias, params.newColumn, params.originalColumn, params.isReplace, params.expressionField);
      } else {
        result = updateEncodedColumnWithExpression(params.alias, params.column, params.isReplace, params.expressionField);
      }
    }
    if (result.error) {
      setError(new Error(result.error));
    } else {
      setError(null);
      onChange(result.updatedReport);
    }
  }, [tableAndPropertyMeta, currentDimensionColumn, allAvailableColumns, relationalReport, allStagedColumns, allDatasetFields, addColumnToChannel, moveColumnToStagedColumns, removeColumnFromReportColumns, updateEncodedColumnWithExpressionAddStaged, updateEncodedColumnWithExpression, onChange]);
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  if (fullTableAndPropertyMetaError) {
    return /*#__PURE__*/_jsx(ErrorAlert, {
      errorMessage: I18n.text('report-customization-components.customization-components.relational-report.dimension-fetch-error'),
      error: fullTableAndPropertyMetaError
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(FormControl, {
      children: /*#__PURE__*/_jsx(ErrorAlert, {
        errorMessage: I18n.text('report-customization-components.customization-components.relational-report.metric-update-error'),
        error: error
      })
    });
  }
  return /*#__PURE__*/_jsx(InputGroup, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "report-customization-components.customization-components.dimension-editor-dropdown.config.label"
    }),
    tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "report-customization-components.customization-components.dimension-editor-dropdown.config.tooltip"
    }),
    use: "itemRight",
    children: /*#__PURE__*/_jsx("div", {
      style: {
        width: '100%'
      },
      children: /*#__PURE__*/_jsx(Select, Object.assign({}, props, {
        clearable: !hideNoneOptionInUISelect,
        dropdownFooter: dropdownFooter,
        buttonSize: "md",
        options: availablePropertiesOptionsWithoutDefaultMeasures,
        value: propertyValue,
        onChange: onPropertyChange,
        disabled: chartType === VisualTypes.GAUGE,
        paddingInlineStart: buttonUse === 'transparent' ? '0' : undefined,
        menuWidth: "auto",
        placeholder: I18n.text('report-customization-components.constants.uiselect.none')
      }))
    })
  });
};
export default RelationalReportDimensionEditorDropdown;